import React, { FC, useEffect, useState } from 'react';
import Autocomplete from '@/components/elements/AutoComplete';
import AppButton from '@/components/atoms/AppButton';
import ConfigInput from '@/components/elements/ConfigInput';
import { useDispatch, useSelector } from 'react-redux';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { useToast } from '@/components/organism/Toast/ToastProvider';

import { Modal } from '@/components/elements/Modal';
import _, { isEmpty } from 'lodash';
import { useForm, SubmitHandler, FieldErrors } from 'react-hook-form';
import { REQUIRED_VALIDATION_MSG } from '@/constant/provider/providerError';
import {
  USER_FORM_EMAIL,
  USER_FORM_FIRST_NAME,
  USER_FORM_LAST_NAME,
  USER_FORM_PASSWORD,
  USER_FORM_ROLE_NAME,
  USER_PROPS,
} from '@/constant/users/user.constant';
import {
  getUsersRoleFailure,
  getUsersRoleSuccess,
} from '@/store/slices/role.slice';
import { updateUser } from '@/services/user';
import {
  updateUserFailure,
  updateUserSuccess,
} from '@/store/slices/user.slice';
import { fetchUserRoles } from '@/services/role';
import {
  COMMON_VALIDATIONS,
  COMMON_VALIDATION_RULES,
  EMAIL_VALIDATION_RULES,
  PASSWORD_VALIDATION_RULES,
  VALIDATION_TYPES,
} from '@/constant/global/global.constant';
import { useNavigate } from 'react-router-dom';
import { IUpdateUser } from '@/services/user/user.type';
import { IRole } from '@/services/role/role.type';
import {
  IEditUserPayload,
  IUserFormInputs,
  UpdateUserProps,
} from './user.interface';
import { tid } from '@/utils/testId';

const EditUser: FC<UpdateUserProps> = ({ isOpen, userData, onClose }) => {
  const { userId, authUserId, rolesDataInfo, firstName, lastName, email } =
    userData;
  const { roleIds, selectedRoleNames, selectedRolesInfo } = rolesDataInfo;
  const [fName, setFName] = useState(firstName);
  const [lName, setLName] = useState(lastName);
  const [emailId, setEmailId] = useState(email);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(roleIds);
  const [searchRole, setSearchRole] = useState('');
  const rolesData = useSelector((state: any) => state?.role?.roles?.data);
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [selectedRole, setSelectedRole] = useState(selectedRolesInfo);
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    getUserRoles();
  }, []);

  const getUserRoles = async () => {
    try {
      const res = await fetchUserRoles({ ...pagination });
      dispatch(getUsersRoleSuccess(res?.data as any));
    } catch (error) {
      dispatch(getUsersRoleFailure(undefined));
      toast?.pushError(
        (error as any)?.response?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };
  const handleSelectedRole = (value: IRole) => {
    const selectedRoleId = value?.roleId;
    if (!_.isUndefined(selectedRoleId) && !role.includes(selectedRoleId)) {
      setSelectedRole((prev) => [...prev, value]);
      setRole((prev) => [...prev, selectedRoleId]);
    } else {
      setRole((prev) => prev.filter((id) => id !== selectedRoleId));
      !_.isUndefined(selectedRoleId) &&
        setSelectedRole((prev) =>
          prev.filter((role) => role?.roleId !== selectedRoleId),
        );
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<IUserFormInputs>();

  const { onBlur: onBlurFirstName } = register(USER_FORM_FIRST_NAME);
  const { onBlur: onBlurLastName } = register(USER_FORM_LAST_NAME);
  const { onBlur: onBlurEmail } = register(USER_FORM_EMAIL);
  const { onBlur: onBlurPassword } = register(USER_FORM_PASSWORD);
  const isPasswordEdited = dirtyFields?.password;
  const isEmailEdited = dirtyFields?.email;

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const handlingErrorMessages = (
    errorMessages: FieldErrors<IUserFormInputs>,
  ) => {
    for (const fieldName in errorMessages) {
      if (errorMessages.hasOwnProperty(fieldName)) {
        const errorMessage =
          errorMessages[fieldName as keyof FieldErrors<IUserFormInputs>];
        if (errorMessage?.type === VALIDATION_TYPES.REQUIRED) {
          toast?.pushError(
            REQUIRED_VALIDATION_MSG,
            PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
          );
        }
      }
    }
  };

  function generateUploadPayload(payload: IEditUserPayload): IEditUserPayload {
    const uploadPayload: IEditUserPayload = {
      firstName: payload?.firstName,
      lastName: payload?.lastName,
      authUserId: payload?.authUserId,
    };
    if (isPasswordEdited) {
      uploadPayload.password = payload?.password;
    } else if (isEmailEdited) {
      uploadPayload.email = payload?.email;
    }
    return uploadPayload;
  }

  useEffect(() => {
    handlingErrorMessages(errors);
  }, [errors]);
  const onSubmit: SubmitHandler<IUserFormInputs> = async (formData) => {
    let payload: IEditUserPayload;
    let uploadPayload: IEditUserPayload;
    if (role?.length > 0 || selectedRolesInfo?.length > 0) {
      payload = { ...formData, authUserId };
      uploadPayload = generateUploadPayload(payload);
      uploadPayload = { ...uploadPayload, roleIds: role };
    } else {
      payload = { ...formData, authUserId };
      uploadPayload = generateUploadPayload(payload);
    }
    try {
      const res = await updateUser(userId, uploadPayload);
      dispatch(updateUserSuccess((res?.data as any)?.data));
      onClose(false);
      toast?.pushSuccess(
        (res?.data as any)?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
      setPassword('');
      navigate('/users');
    } catch (error) {
      dispatch(updateUserFailure(undefined));
      toast?.pushError(
        (error as any)?.response?.data?.message || (error as any)?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };

  return (
    <Modal open={isOpen} onClose={() => onClose(false)} size="lg">
      <Modal.Header title={USER_PROPS.EDIT_USER} />
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-128 mb-6 px-4 pt-4 pb-2">
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`edit-user-${USER_PROPS.FIRST_NAME.LABEL}`)}
              label={USER_PROPS.FIRST_NAME.LABEL}
              placeholder={USER_PROPS.FIRST_NAME.PLACEHOLDER}
              onChange={(e) => {
                onBlurFirstName(e);
                setFName(e.target.value);
              }}
              register={register}
              validationSchema={COMMON_VALIDATION_RULES}
              value={fName}
              name="firstName"
              error={!!errors?.firstName}
              errorMsg={errors?.firstName && errors?.firstName?.message}
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`edit-user-${USER_PROPS.LAST_NAME.LABEL}`)}
              label={USER_PROPS.LAST_NAME.LABEL}
              placeholder={USER_PROPS.LAST_NAME.PLACEHOLDER}
              onChange={(e) => {
                onBlurLastName(e);
                setLName(e.target.value);
              }}
              register={register}
              validationSchema={COMMON_VALIDATION_RULES}
              value={lName}
              name="lastName"
              error={!!errors?.lastName}
              errorMsg={errors?.lastName && errors?.lastName?.message}
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`edit-user-${USER_PROPS.EMAIL_ID.LABEL}`)}
              label={USER_PROPS.EMAIL_ID.LABEL}
              placeholder={
                isPasswordEdited
                  ? USER_PROPS.DISCLAIMER.CANNOT_UPDATE_BOTH
                  : USER_PROPS.PASSWORD.PLACEHOLDER
              }
              value={
                isPasswordEdited
                  ? USER_PROPS.DISCLAIMER.CANNOT_UPDATE_BOTH
                  : emailId
              }
              name={USER_FORM_EMAIL}
              type={USER_FORM_EMAIL}
              onChange={(e) => {
                setEmailId(e.target.value);
                onBlurEmail(e);
              }}
              disabled={isPasswordEdited}
              register={register}
              validationSchema={{
                required: {
                  value: isEmailEdited,
                  message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
                },
                EMAIL_VALIDATION_RULES,
              }}
              error={!isPasswordEdited && !!errors?.email}
              errorMsg={
                !isPasswordEdited
                  ? (errors?.email?.message ?? undefined)
                  : undefined
              }
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`edit-user-${USER_PROPS.PASSWORD.LABEL}`)}
              label={USER_PROPS.PASSWORD.LABEL}
              placeholder={
                isEmailEdited
                  ? USER_PROPS.DISCLAIMER.CANNOT_UPDATE_BOTH
                  : USER_PROPS.PASSWORD.PLACEHOLDER
              }
              value={password}
              name={USER_FORM_PASSWORD}
              type={USER_FORM_PASSWORD}
              onChange={(e) => {
                setPassword(e.target.value);
                onBlurPassword(e);
              }}
              disabled={isEmailEdited}
              register={register}
              validationSchema={{
                ...PASSWORD_VALIDATION_RULES,
                required: {
                  value: isPasswordEdited,
                  message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
                },
              }}
              error={!isEmailEdited && !!errors?.password}
              errorMsg={
                !isEmailEdited
                  ? (errors?.password?.message ?? undefined)
                  : undefined
              }
            />
            <Autocomplete
              multiSelect
              nameKey={USER_FORM_ROLE_NAME}
              dataTestId={tid(`edit-user-${USER_PROPS.ROLE.PLACEHOLDER}`)}
              placeholder={USER_PROPS.ROLE.PLACEHOLDER}
              label={USER_PROPS.ROLE.LABEL}
              selectedValue={selectedRole}
              handleSelectedValue={handleSelectedRole as any}
              handleSearch={setSearchRole}
              lists={rolesData}
            />
          </div>
          <div className="grid grid-cols-1 max-lg:grid-cols-1 gap-6 px-2 py-2">
            <AppButton
              tabIndex={3}
              data-testid={tid('user-edit-save-btn')}
              type="submit"
              variant="primary"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? USER_PROPS.BUTTON.WHILE_LOADING_IN_UPDATION
                : USER_PROPS.BUTTON.EDIT}
            </AppButton>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditUser;
